import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import { Button, IconButton, Popover, Typography } from '@mui/material';

import { LogLinkApi } from '../../../../../../api';
import modalBoxes from '../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import { scBlue100, scGray50 } from '../../../../../../constants/systemColors';
import { onRequestError } from '../../../../../../services/handlers';
import { useCurrentRoute } from '../../../../../root/router';
import { isLogWasLinked, normalizeSubjectIds } from '../LogsPageServices';

import LinkLogRecordModal from './LinkLogRecordModal/LinkLogRecordModal';

export const LinkLogButton = ({
  studyId,
  itemGroupTemplateId,
  itemGroupTemplateVersionId,
  rowId,
  patientInfo,
  loadLinkedSubjects,
  preparedLinkedSubjectIDs,
  onCloseModalCallback,
  isLinked,
  label = ''
}) => {
  const {
    params: { ssuPatientId }
  } = useCurrentRoute();
  const [linkedSubjectIDs, setLinkedSubjectIDs] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [logWasLinked, setLogWasLinked] = useState(false);

  const getLinkedSubjectIDs = useCallback(() => {
    LogLinkApi.getLinkedSubjectIDs({
      studyId,
      ssuPatientId,
      itemGroupTemplateId,
      itemGroupTemplateVersionId,
      rowIds: [rowId]
    }).then(({ data }) => {
      setLinkedSubjectIDs(normalizeSubjectIds(data)[rowId]);
      setLogWasLinked(isLogWasLinked(data, rowId));
    }, onRequestError);
  }, [itemGroupTemplateId, itemGroupTemplateVersionId, rowId, ssuPatientId, studyId]);

  const onCloseModal = useCallback(() => {
    if (preparedLinkedSubjectIDs) {
      onCloseModalCallback();
    } else {
      getLinkedSubjectIDs();
      onCloseModalCallback();
    }
  }, [getLinkedSubjectIDs, onCloseModalCallback, preparedLinkedSubjectIDs]);

  useEffect(() => {
    if (loadLinkedSubjects) {
      getLinkedSubjectIDs();
    } else {
      setLinkedSubjectIDs(preparedLinkedSubjectIDs);
      setLogWasLinked(isLinked);
    }
  }, [getLinkedSubjectIDs, isLinked, loadLinkedSubjects, preparedLinkedSubjectIDs]);

  const handlePopoverOpen = useCallback(
    event => {
      if (linkedSubjectIDs) {
        setAnchorEl(event.currentTarget);
      }
    },
    [linkedSubjectIDs]
  );

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onLinkClick = useCallback(() => {
    modalBoxes.open({
      title: 'Link Log Record: Protocol Deviation Log',
      component: (
        <LinkLogRecordModal
          studyId={studyId}
          itemGroupTemplateId={itemGroupTemplateId}
          itemGroupTemplateVersionId={itemGroupTemplateVersionId}
          rowId={rowId}
          patientInfo={patientInfo}
          ssuPatientId={ssuPatientId}
        />
      ),
      size: 'full-screen',
      className: 'link-log-record-modal',
      onClose: onCloseModal
    });
  }, [itemGroupTemplateId, itemGroupTemplateVersionId, onCloseModal, patientInfo, rowId, ssuPatientId, studyId]);

  const buttonStyles = useMemo(() => (logWasLinked ? { color: 'info' } : { sx: { color: scGray50 } }), [logWasLinked]);

  return (
    <>
      {label ? (
        <Button
          data-testid="link-log-button-with-label"
          onClick={onLinkClick}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          variant="text"
          startIcon={<LinkIcon />}
          {...buttonStyles}
        >
          {label}
        </Button>
      ) : (
        <IconButton
          data-testid="link-log-button"
          onClick={onLinkClick}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          sx={logWasLinked ? { color: scBlue100 } : {}}
        >
          <LinkIcon data-testid="LinkIcon" />
        </IconButton>
      )}
      <Popover
        sx={{ pointerEvents: 'none' }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Typography sx={{ p: 1 }} data-testid="list-of-linked-subjectids">
          {linkedSubjectIDs}
        </Typography>
      </Popover>
    </>
  );
};
